import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid2 as Grid,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import axios from "axios";
import UploadedFormsTable from "./UploadedFormsTable";
import DownloadTemplateDialog from "./DownloadTemplateDialog";
import PDFFormEditor from "../editor/PDFFormEditor";
import FormSettingsModal from "../editor/components/FormSettingsModal";
import SyncDocumentDialog from "../components/SyncDocumentDialog";
import SyncedFormEditDialog from "../components/SyncedFormEditDialog";
import {
  acquireFormEditLock,
  formatFormEditLockedMessage,
  isFormEditLockLostError,
  releaseFormEditLock as releaseFormEditLockRequest,
  renewFormEditLock,
} from "../editor/formEditLockApi";

const normalizeFieldType = (rawType) => {
  const type = String(rawType || "text")
    .trim()
    .toLowerCase();

  if (["checkbox", "pdfcheckbox", "check", "check_box"].includes(type)) {
    return "PDFCheckBox";
  }

  if (["radio", "pdfradiogroup", "radio_group", "radiogroup"].includes(type)) {
    return "PDFRadioGroup";
  }

  if (
    [
      "text",
      "textfield",
      "pdftextfield",
      "input",
      "textbox",
      "date",
      "select",
    ].includes(type)
  ) {
    return "PDFTextField";
  }

  return rawType || "PDFTextField";
};

const UploadedForms = ({ onError, onSuccess, onNotify, currentSession }) => {
  const fetchOptionsRef = useRef(null);
  const currentEditLockRef = useRef(null);

  // State management
  const [forms, setForms] = useState([]);
  const [jurisdictions, setJurisdictions] = useState({
    countries: [],
    states: [],
    counties: [],
    cities: [],
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const selectedCountry = "jur_us";
  const [selectedState, setSelectedState] = useState("jur_us_fl");
  const [selectedCounty, setSelectedCounty] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [deleteLoading, setDeleteLoading] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [stateFormsOnly, setStateFormsOnly] = useState(false);
  const [countyFormsOnly, setCountyFormsOnly] = useState(false);
  const [inactiveFormsOnly, setInactiveFormsOnly] = useState(false);
  const [syncedFormsOnly, setSyncedFormsOnly] = useState(false);
  const [unsyncedFormsOnly, setUnsyncedFormsOnly] = useState(false);
  const [nocFilter, setNocFilter] = useState("include"); // "include", "exclude", "only"
  const [sortField, setSortField] = useState("_id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [downloadLoading, setDownloadLoading] = useState("");
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedFormForDownload, setSelectedFormForDownload] = useState(null);
  const [pdfEditorOpen, setPdfEditorOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [editLockLost, setEditLockLost] = useState(false);
  const [selectedFormForEdit, setSelectedFormForEdit] = useState(null);
  const [formData, setFormData] = useState(null);
  const [jurisdictionOptions, setJurisdictionOptions] = useState([]);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedFormForSettings, setSelectedFormForSettings] = useState(null);
  const [isFinalizedEditMode, setIsFinalizedEditMode] = useState(false);
  const [finalizedEditInitialView, setFinalizedEditInitialView] =
    useState("preview");
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [selectedFormForSync, setSelectedFormForSync] = useState(null);
  const [syncedEditDialogOpen, setSyncedEditDialogOpen] = useState(false);
  const [pendingSyncedEdit, setPendingSyncedEdit] = useState(null);

  // Use ref to avoid dependency issues with error handlers
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);

  // Update refs when props change
  useEffect(() => {
    onErrorRef.current = onError;
    onSuccessRef.current = onSuccess;
  }, [onError, onSuccess]);

  useEffect(() => {
    const fetchJurisdictionOptions = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/jurisdictions/florida/counties-municipalities`,
          { currentSession },
          { withCredentials: true },
        );

        if (response?.data?.success) {
          setJurisdictionOptions(response.data.data || []);
        } else {
          onError("Failed to load counties and municipalities.");
        }
      } catch (error) {
        console.error("Failed to fetch jurisdiction options:", error);
        onError("Failed to load counties and municipalities.");
      }
    };

    if (!fetchOptionsRef?.current) {
      fetchOptionsRef.current = true;
      fetchJurisdictionOptions();
    }
  }, [currentSession, onError]);

  // Fetch jurisdictions hierarchically
  const fetchJurisdictions = useCallback(
    async (countryId = null, stateId = null, countyId = null) => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/jurisdictions/hierarchical`,
          {
            currentSession,
            selected_country: countryId,
            selected_state: stateId,
            selected_county: countyId,
          },
          { withCredentials: true },
        );

        if (response.data.success) {
          setJurisdictions({
            countries: response.data.data.countries || [],
            states: response.data.data.states || [],
            counties: response.data.data.counties || [],
            cities: response.data.data.cities || [],
          });
        }
      } catch (error) {
        console.error("Error fetching jurisdictions:", error);
      }
    },
    [currentSession],
  );

  // Fetch forms data with optimized dependency tracking
  const fetchForms = useCallback(async () => {
    try {
      setLoading(true);

      // Determine which jurisdiction to use for filtering based on level filters
      let jurisdictionFilter = null;
      let levelFilter = null;
      const statusFilter = inactiveFormsOnly ? "inactive" : "active";
      const syncFilter = inactiveFormsOnly
        ? "all"
        : syncedFormsOnly
          ? "synced"
          : unsyncedFormsOnly
            ? "unsynced"
            : "all";

      if (stateFormsOnly) {
        // State forms only - jurisdiction_id should match selected state and country
        jurisdictionFilter = selectedState;
        levelFilter = "state";
      } else if (countyFormsOnly) {
        // County forms only - jurisdiction_id should match selected county (level 2)
        jurisdictionFilter = selectedCounty || selectedState;
        levelFilter = "county";
      } else {
        // Default behavior - use the most specific jurisdiction selected
        if (selectedCity) {
          jurisdictionFilter = selectedCity; // Level 3 - City
        } else if (selectedCounty) {
          jurisdictionFilter = selectedCounty; // Level 2 - County
        } else if (selectedState) {
          jurisdictionFilter = selectedState; // Level 1 - State
        } else if (selectedCountry) {
          jurisdictionFilter = selectedCountry; // Level 0 - Country
        }
      }

      // Add request cancellation for better performance
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/list`,
        {
          page: page + 1,
          limit: rowsPerPage,
          jurisdiction_id: jurisdictionFilter,
          level_filter: levelFilter,
          noc_filter: nocFilter,
          status_filter: statusFilter,
          sync_filter: syncFilter,
          sort_field: sortField,
          sort_direction: sortDirection,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
          signal: controller.signal,
          timeout: 30000, // 30 second timeout
        },
      );

      clearTimeout(timeoutId);

      if (response.data.success) {
        setForms(response.data.data.forms);
        setTotalCount(response.data.data.total);
      } else {
        onErrorRef.current("Failed to fetch forms");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching forms:", error);
        onErrorRef.current("Error fetching forms");
      }
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    selectedCity,
    selectedCounty,
    selectedState,
    selectedCountry,
    stateFormsOnly,
    countyFormsOnly,
    inactiveFormsOnly,
    syncedFormsOnly,
    unsyncedFormsOnly,
    nocFilter,
    sortField,
    sortDirection,
    currentSession,
  ]);

  useEffect(() => {
    fetchJurisdictions(); // Load all jurisdictions initially
  }, [fetchJurisdictions]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // Memoize expensive computations
  const memoizedForms = useMemo(
    () =>
      forms.map((form) => ({
        ...form,
        canDelete: inactiveFormsOnly,
      })),
    [forms, inactiveFormsOnly],
  );
  const memoizedJurisdictions = useMemo(() => jurisdictions, [jurisdictions]);

  // Memoize filter options to prevent unnecessary re-renders
  const countyOptions = useMemo(() => {
    const sortedCounties = [...jurisdictions.counties].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );

    return [{ id: "", name: "All Counties" }, ...sortedCounties];
  }, [jurisdictions.counties]);

  const cityOptions = useMemo(() => {
    const sortedCities = [...jurisdictions.cities].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );

    return [{ id: "", name: "All Municipality" }, ...sortedCities];
  }, [jurisdictions.cities]);

  const handleStateChange = (event) => {
    const newStateId = event.target.value;
    setSelectedState(newStateId);
    setSelectedCounty("");
    setSelectedCity("");
    setPage(0);
    // Fetch jurisdictions with new state selection
    fetchJurisdictions(selectedCountry, newStateId, null);
  };

  const handleCountyChange = (event) => {
    const newCountyId = event.target.value;
    setSelectedCounty(newCountyId);
    setSelectedCity("");
    setPage(0);
    // Fetch jurisdictions with new county selection
    fetchJurisdictions(selectedCountry, selectedState, newCountyId);
  };

  const handleCityChange = (event) => {
    setSelectedCity(event.target.value);
    setPage(0);
  };

  // Handle level filter changes
  const handleStateFormsChange = (event) => {
    const checked = event.target.checked;
    setStateFormsOnly(checked);
    if (checked) {
      setCountyFormsOnly(false);
      setSelectedCounty("");
      setSelectedCity("");
    }
    setPage(0);
  };

  const handleCountyFormsChange = (event) => {
    const checked = event.target.checked;
    setCountyFormsOnly(checked);
    if (checked) {
      setStateFormsOnly(false);
      setSelectedCity("");
    }
    setPage(0);
  };

  const handleInactiveFormsChange = (event) => {
    const checked = event.target.checked;
    setInactiveFormsOnly(checked);
    if (checked) {
      setSyncedFormsOnly(false);
      setUnsyncedFormsOnly(false);
    }
    setPage(0);
  };

  const handleSyncedFormsChange = (event) => {
    const checked = event.target.checked;
    setSyncedFormsOnly(checked);
    if (checked) {
      setUnsyncedFormsOnly(false);
    }
    setPage(0);
  };

  const handleUnsyncedFormsChange = (event) => {
    const checked = event.target.checked;
    setUnsyncedFormsOnly(checked);
    if (checked) {
      setSyncedFormsOnly(false);
    }
    setPage(0);
  };

  const handleOpenSyncDialog = useCallback((form) => {
    setSelectedFormForSync(form);
    setSyncDialogOpen(true);
  }, []);

  const handleCloseSyncDialog = useCallback(() => {
    setSyncDialogOpen(false);
    setSelectedFormForSync(null);
  }, []);

  const handleSyncDocument = useCallback(
    async ({ password, confirmed }) => {
      if (!selectedFormForSync?.form_id) {
        throw new Error("No form ID found");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/sync-to-production`,
        {
          formId: selectedFormForSync.form_id,
          syncPassword: password,
          confirmedFullyTested: confirmed,
          currentSession,
        },
        { withCredentials: true, timeout: 120000 },
      );

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message || "Document synchronization failed",
        );
      }

      onSuccessRef.current(
        response.data.message ||
          "Document synchronized to production successfully",
      );
      handleCloseSyncDialog();
      await fetchForms();
    },
    [currentSession, fetchForms, handleCloseSyncDialog, selectedFormForSync],
  );

  // Handle NOC filter changes
  const handleNocFilterChange = (event) => {
    setNocFilter(event.target.value);
    setPage(0);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0);
  };

  // Handle download button click - open dialog
  const handleDownloadClick = useCallback((formId) => {
    setSelectedFormForDownload({ formId });
    setDownloadDialogOpen(true);
  }, []);

  const releaseEditLock = useCallback(
    async (lock = currentEditLockRef.current) => {
      if (!lock?.formId || !lock?.lockId) return;
      if (
        currentEditLockRef.current?.formId === lock.formId &&
        currentEditLockRef.current?.lockId === lock.lockId
      ) {
        currentEditLockRef.current = null;
      }
      try {
        await releaseFormEditLockRequest({
          currentSession,
          formId: lock.formId,
          lockId: lock.lockId,
        });
      } catch (error) {
        if (error?.response?.status !== 409) {
          console.error("Failed to release form editor lock:", error);
        }
      }
    },
    [currentSession],
  );

  const recoverEditLock = useCallback(async () => {
    const formId =
      currentEditLockRef.current?.formId ||
      selectedFormForEdit ||
      formData?.formId ||
      "";
    if (!formId) return false;

    const recoveredLock = await acquireFormEditLock({ currentSession, formId });
    currentEditLockRef.current = recoveredLock;
    setEditLockLost(false);
    setFormData((prev) => ({ ...(prev || {}), editLock: recoveredLock }));
    return recoveredLock;
  }, [currentSession, formData?.formId, selectedFormForEdit]);

  // Handle edit button click - open PDF editor
  const handleEditClick = useCallback(
    async (
      formId,
      fallbackLabel = "",
      county,
      jurisdictions_id,
      status,
      doc,
      signatures,
      editorOptions = {},
    ) => {
      let acquiredLock = null;
      try {
        setLoading(true);

        // Fetch form data including PDF and form fields
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-instances/get-form-data`,
          { formId },
          { withCredentials: true },
        );

        if (response.data.success) {
          setSelectedFormForEdit(formId);
          // Normalize form fields from backend if present
          const data = response.data.data || {};
          const resolvedFormLabel =
            fallbackLabel ||
            data.label ||
            data.formName ||
            data.guessedFormDetails?.label_guess ||
            data.fileName ||
            "Untitled Form";
          const fields = Array.isArray(data.formFields) ? data.formFields : [];
          let normalized = fields.map((f) => ({
            ...f,
            type: normalizeFieldType(f.type),
            x: Number(f.x ?? f.rect?.X ?? f.rect?.x ?? 0),
            y: Number(f.y ?? f.rect?.Y ?? f.rect?.y ?? 0),
            width: Number(f.width ?? f.rect?.W ?? f.rect?.width ?? 0),
            height: Number(f.height ?? f.rect?.H ?? f.rect?.height ?? 0),
            page: Number(f.page ?? f.page_num ?? f.pageIndex ?? 1),
            page_num: Number(f.page_num ?? f.page ?? f.pageIndex ?? 1),
            rect: {
              W: Number(f.rect?.W ?? f.width ?? f.rect?.width ?? 0),
              H: Number(f.rect?.H ?? f.height ?? f.rect?.height ?? 0),
              X: Number(f.rect?.X ?? f.x ?? f.rect?.x ?? 0),
              Y: Number(f.rect?.Y ?? f.y ?? f.rect?.y ?? 0),
              S: Array.isArray(f.rect?.S)
                ? f.rect.S.map((v) => Number(v)).filter((v) =>
                    Number.isFinite(v),
                  )
                : [],
            },
            field_label:
              f.field_label || f.label || f.name || f.pdf_field_name || "",
            field_required: Boolean(f.field_required),
          }));

          // Convert Blob to File object like UploadBox does
          let pdfFile = null;
          try {
            const pdfRes = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/nocApi/forms/download`,
              { formId, downloadType: "pdf" },
              { withCredentials: true, responseType: "blob" },
            );
            const blob = pdfRes.data;
            // Convert Blob to File object
            pdfFile = new File([blob], `${formId}.pdf`, {
              type: "application/pdf",
            });
          } catch (e) {
            console.error("PDF fetch for render failed:", e);
          }

          const municipality = cityOptions.find(
            (c) => c.id === jurisdictions_id,
          )?.name;

          const countyData = jurisdictionOptions?.find(
            (opt) => opt.county_name === county,
          );
          const municipalitySlug = countyData?.municipality?.find(
            (opt) => opt.mun_name === municipality,
          )?.mun_slug;

          if (status === "inactive") {
            acquiredLock = await acquireFormEditLock({
              currentSession,
              formId,
            });
            currentEditLockRef.current = acquiredLock;
            setEditLockLost(false);
          }

          setFormData({
            ...data,
            formId: data.form_id || formId,
            formName: resolvedFormLabel,
            jurisdictions_id: data.jurisdictions_id || jurisdictions_id,
            selected_permit_type:
              data.selected_permit_type ??
              editorOptions.selectedPermitType ??
              "",

            permit_types: data.permit_types?.length
              ? data.permit_types
              : editorOptions.permitTypes || [],
            fileName: data.fileName || resolvedFormLabel,
            guessedFormDetails: {
              county: countyData?.county_slug,
              municipality: municipalitySlug,
              isStatewideForm: !!!county,
              jurisdictionLevel: municipality ? "municipality" : "county",
              docSize: doc?.size || "",
              isNoc: doc?.is_noc ?? false,
              signatures,
              ...(data.guessedFormDetails || {}),
              label_guess:
                data.guessedFormDetails?.label_guess || resolvedFormLabel,
            },
            pdfFile,
            base64File: undefined,
            formFields: normalized,
            municipality,
            county,
            status,
            state: selectedState,
            syncedFormEditToken: editorOptions.syncedFormEditToken || "",
            editLock: acquiredLock,
          });

          // Close side navigation only when PDF is ready to render
          window.dispatchEvent(new Event("closeSideNav"));
          window.dispatchEvent(new Event("forceHideSideNavOn"));
          window.dispatchEvent(new Event("forceHideAppChromeOn"));
          setIsFinalizedEditMode(Boolean(editorOptions.finalizedEditMode));
          setFinalizedEditInitialView(editorOptions.initialView || "preview");
          setPdfEditorOpen(true);
        } else {
          if (acquiredLock) {
            await releaseEditLock(acquiredLock);
          }
          onErrorRef.current("Failed to load form data");
        }
      } catch (error) {
        console.error("Error loading form data:", error);
        if (acquiredLock) {
          await releaseEditLock(acquiredLock);
        }
        onErrorRef.current(
          error?.response?.status === 409
            ? formatFormEditLockedMessage(error)
            : error?.response?.data?.message || "Error loading form data",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      cityOptions,
      currentSession,
      jurisdictionOptions,
      releaseEditLock,
      selectedState,
    ],
  );

  const closePdfEditor = useCallback(async () => {
    await releaseEditLock();
    window.dispatchEvent(new Event("forceHideSideNavOff"));
    window.dispatchEvent(new Event("forceHideAppChromeOff"));
    setConfirmDiscardOpen(false);
    setPdfEditorOpen(false);
    setEditLockLost(false);
    setSelectedFormForEdit(null);
    setFormData(null);
    setIsFinalizedEditMode(false);
    setFinalizedEditInitialView("preview");
  }, [releaseEditLock]);

  // Handle PDF editor close
  const handlePdfEditorClose = useCallback(
    (hasUnsavedChanges = false) => {
      if (hasUnsavedChanges) {
        setConfirmDiscardOpen(true);
        return;
      }

      return closePdfEditor();
    },
    [closePdfEditor],
  );

  const handlePdfEditorFinalizeSuccess = useCallback(async () => {
    await closePdfEditor();
    await fetchForms();
  }, [closePdfEditor, fetchForms]);

  // Handle PDF editor save
  const handlePdfEditorSave = useCallback(
    async (updatedFormData, saveOptions = {}) => {
      try {
        setLoading(true);

        let currentLock = currentEditLockRef.current;
        if (currentLock?.formId && currentLock?.lockId) {
          try {
            await renewFormEditLock({
              currentSession,
              formId: currentLock.formId,
              lockId: currentLock.lockId,
            });
          } catch (error) {
            if (isFormEditLockLostError(error)) {
              try {
                currentLock = await recoverEditLock();
              } catch (recoveryError) {
                setEditLockLost(true);
                onErrorRef.current(
                  recoveryError?.response?.status === 409
                    ? formatFormEditLockedMessage(recoveryError)
                    : recoveryError?.response?.data?.message ||
                        "The edit lock could not be recovered. Your changes remain in the editor; try saving again.",
                );
                return false;
              }
            } else {
              onErrorRef.current(
                "The edit session could not be confirmed because of a connection problem. Your changes remain in the editor; check your connection and save again.",
              );
              return false;
            }
          }
        }

        const {
          title = updatedFormData?.guessedFormDetails?.label_guess ||
            updatedFormData?.formName ||
            updatedFormData?.fileName ||
            formData?.formName ||
            "",
          isClose = false,
          silent = false,
        } = saveOptions || {};

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-instances/save-form-data`,
          {
            form_id: selectedFormForEdit,
            formLabel: title,
            instances: updatedFormData?.formFields?.map((f) => {
              const blockId = f?.block_id ? f.block_id.split("__sub")[0] : "";
              if (f?.field_instance_id) {
                return {
                  ...f,
                  value: "",
                  rect: {
                    W: f?.width,
                    H: f?.height,
                    X: f?.x,
                    Y: f?.y,
                  },
                  block_id: blockId,
                };
              } else {
                return {
                  ...f,
                  value: "",
                  form_id: selectedFormForEdit,
                  page_num: f?.page,
                  rect: {
                    W: f?.width,
                    H: f?.height,
                    X: f?.x,
                    Y: f?.y,
                  },
                  field_label: f?.field_label ?? f?.label ?? f?.name,
                  block_id: blockId,
                };
              }
            }),
            syncedFormEditToken: formData?.syncedFormEditToken || "",
            lockId: currentLock?.lockId || formData?.editLock?.lockId || "",
          },
          { withCredentials: true },
        );

        if (response.data.success) {
          if (!silent) {
            onSuccessRef.current("Form updated successfully");
          }
          if (isClose) {
            await releaseEditLock();
            window.dispatchEvent(new Event("forceHideSideNavOff"));
            window.dispatchEvent(new Event("forceHideAppChromeOff"));
            setPdfEditorOpen(false);
            setSelectedFormForEdit(null);
            setFormData(null);
            setIsFinalizedEditMode(false);
            setFinalizedEditInitialView("preview");
          }
          fetchForms(); // Refresh the forms list
          return true;
        } else {
          onErrorRef.current("Failed to save form changes");
          return false;
        }
      } catch (error) {
        console.error("Error saving form data:", error);
        if (
          error?.response?.status === 409 &&
          error?.response?.data?.code === "FORM_EDIT_LOCK_LOST"
        ) {
          setEditLockLost(true);
        }
        onErrorRef.current(
          error?.response?.data?.message || "Error saving form changes",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedFormForEdit,
      fetchForms,
      formData?.editLock?.lockId,
      formData?.syncedFormEditToken,
      recoverEditLock,
      releaseEditLock,
    ],
  );

  // Handle actual download
  const handleDownload = useCallback(
    async (formId, downloadType) => {
      try {
        if (downloadLoading === formId) {
          return;
        }
        setDownloadLoading(formId);

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/forms/download`,
          { formId, downloadType },
          {
            withCredentials: true,
            responseType: "blob",
          },
        );

        // Handle different response types
        if (
          downloadType === "image" &&
          response.headers["content-type"] === "application/zip"
        ) {
          // For images, we might get a zip file with multiple images
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${formId}_images.zip`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } else {
          // For PDF or single image
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${formId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }

        setDownloadDialogOpen(false);
        setSelectedFormForDownload(null);
      } catch (error) {
        console.error("Download error:", error);
        onErrorRef.current("Download failed");
      } finally {
        setDownloadLoading(false);
      }
    },
    [downloadLoading],
  );

  // Handle download dialog close
  const handleDownloadDialogClose = useCallback(() => {
    if (!downloadLoading) {
      setDownloadDialogOpen(false);
      setSelectedFormForDownload(null);
    }
  }, [downloadLoading]);

  // Handle delete confirmation
  const handleDeleteClick = useCallback((formId, fileName) => {
    setFileToDelete({ formId, fileName });
    setDeleteConfirmOpen(true);
  }, []);

  // Handle file deletion
  const handleDelete = useCallback(async () => {
    if (!fileToDelete?.formId) return;

    try {
      setDeleteLoading(fileToDelete.formId);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/delete`,
        {
          formId: fileToDelete.formId,
          currentSession,
        },
        { withCredentials: true },
      );

      if (response.data.success) {
        onSuccessRef.current("Inactive form deleted successfully");
        setDeleteConfirmOpen(false);
        setFileToDelete(null);
        fetchForms();
      } else {
        onErrorRef.current(response.data.message || "Failed to delete form");
      }
    } catch (error) {
      console.error("Delete form error:", error);
      onErrorRef.current("Failed to delete form");
    } finally {
      setDeleteLoading("");
    }
  }, [fileToDelete, currentSession, fetchForms]);

  // Handle delete confirmation cancel
  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  }, []);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenSettings = useCallback((form) => {
    setSelectedFormForSettings(form);
    setSettingsModalOpen(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setSettingsModalOpen(false);
    setSelectedFormForSettings(null);
  }, []);

  const handleOpenFinalizedEditor = useCallback(
    async (form, initialView, syncedFormEditToken = "") => {
      if (!form?.form_id) {
        onErrorRef.current("No form ID found.");
        return;
      }

      setSettingsModalOpen(false);
      setSelectedFormForSettings(null);

      await handleEditClick(
        form.form_id,
        form.label || form.fileName,
        form.county,
        form.jurisdictions_id,
        form.status,
        form.doc,
        form.signatures,
        {
          finalizedEditMode: true,
          initialView,
          selectedPermitType: form.selected_permit_type || "",
          permitTypes: form.permit_types || [],
          syncedFormEditToken,
        },
      );
    },
    [handleEditClick],
  );

  const handleRequestFinalizedEditor = useCallback(
    (form, initialView) => {
      if (form?.production_sync?.synced === true) {
        setPendingSyncedEdit({ form, initialView });
        setSyncedEditDialogOpen(true);
        return;
      }
      handleOpenFinalizedEditor(form, initialView);
    },
    [handleOpenFinalizedEditor],
  );

  const handleCloseSyncedEditDialog = useCallback(() => {
    setSyncedEditDialogOpen(false);
    setPendingSyncedEdit(null);
  }, []);

  const handleAuthorizeSyncedEdit = useCallback(
    async (password) => {
      const form = pendingSyncedEdit?.form;
      if (!form?.form_id) throw new Error("No form ID found");

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/authorize-synced-form-edit`,
        {
          formId: form.form_id,
          syncPassword: password,
          currentSession,
        },
        { withCredentials: true },
      );
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || "Authorization failed");
      }

      const token = response?.data?.data?.authorizationToken || "";
      const initialView = pendingSyncedEdit?.initialView || "preview";
      handleCloseSyncedEditDialog();
      await handleOpenFinalizedEditor(form, initialView, token);
    },
    [
      currentSession,
      handleCloseSyncedEditDialog,
      handleOpenFinalizedEditor,
      pendingSyncedEdit,
    ],
  );

  const handleUpdateFinalizedDocument = useCallback(
    (form) => handleRequestFinalizedEditor(form, "preview"),
    [handleRequestFinalizedEditor],
  );

  const handleOpenFinalizedDocumentSettings = useCallback(
    (form) => handleRequestFinalizedEditor(form, "settings"),
    [handleRequestFinalizedEditor],
  );

  return (
    <Box>
      {/* Filters and Actions */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              value={
                memoizedJurisdictions.states.find(
                  (s) => s.id === selectedState,
                ) || null
              }
              onChange={(event, newValue) => {
                handleStateChange({ target: { value: newValue?.id || "" } });
              }}
              options={memoizedJurisdictions.states}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  variant="filled"
                  size="small"
                  sx={{
                    "& .MuiFilledInput-root": {
                      backgroundColor: "white",
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "primary.main",
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              value={
                selectedCounty
                  ? memoizedJurisdictions.counties.find(
                      (c) => c.id === selectedCounty,
                    ) || null
                  : null
              }
              onChange={(event, newValue) => {
                handleCountyChange({ target: { value: newValue?.id || "" } });
              }}
              options={countyOptions}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="County"
                  variant="filled"
                  size="small"
                  sx={{
                    "& .MuiFilledInput-root": {
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: "white",
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              value={
                selectedCity
                  ? memoizedJurisdictions.cities.find(
                      (c) => c.id === selectedCity,
                    ) || null
                  : null
              }
              onChange={(event, newValue) => {
                handleCityChange({ target: { value: newValue?.id || "" } });
              }}
              options={cityOptions}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Municipality"
                  variant="filled"
                  size="small"
                  sx={{
                    "& .MuiFilledInput-root": {
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: "white",
                      "&:hover": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            {/* NOC Filter */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={nocFilter}
                onChange={handleNocFilterChange}
                sx={{
                  backgroundColor: "white",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                }}
              >
                <MenuItem value="include">Include NOC</MenuItem>
                <MenuItem value="exclude">Exclude NOC</MenuItem>
                <MenuItem value="only">NOC Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Level Filters */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={stateFormsOnly}
                onChange={handleStateFormsChange}
                color="primary"
              />
            }
            label="State Forms"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={countyFormsOnly}
                onChange={handleCountyFormsChange}
                color="primary"
              />
            }
            label="County Forms"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={inactiveFormsOnly}
                onChange={handleInactiveFormsChange}
                color="primary"
              />
            }
            label="Inactive Forms"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={syncedFormsOnly}
                onChange={handleSyncedFormsChange}
                color="primary"
                disabled={inactiveFormsOnly}
              />
            }
            label="Synced Forms"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={unsyncedFormsOnly}
                onChange={handleUnsyncedFormsChange}
                color="primary"
                disabled={inactiveFormsOnly}
              />
            }
            label="Unsynced Forms"
          />
        </Box>
      </Box>

      {/* Forms Table */}
      <UploadedFormsTable
        forms={memoizedForms}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onDownload={handleDownloadClick}
        onEdit={handleEditClick}
        onOpenSettings={handleOpenSettings}
        onDelete={handleDeleteClick}
        deleteLoading={deleteLoading}
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        downloadLoading={downloadLoading}
        fetchForms={fetchForms}
        onSync={handleOpenSyncDialog}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the form "{fileToDelete?.fileName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The file will be removed permanently.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading === fileToDelete?.formId}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleteLoading === fileToDelete?.formId}
            startIcon={
              deleteLoading === fileToDelete?.formId ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteLoading === fileToDelete?.formId ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure? All changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscardOpen(false)}>
            No, keep editing
          </Button>
          <Button onClick={closePdfEditor} variant="contained" color="error">
            Yes, discard changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Template Dialog */}
      <DownloadTemplateDialog
        open={downloadDialogOpen}
        onClose={handleDownloadDialogClose}
        onDownload={handleDownload}
        formId={selectedFormForDownload?.formId}
        loading={downloadLoading === selectedFormForDownload?.formId}
      />

      {/* PDF Form Editor */}
      <PDFFormEditor
        formData={formData}
        onClose={handlePdfEditorClose}
        onFinalizeSuccess={handlePdfEditorFinalizeSuccess}
        onSave={handlePdfEditorSave}
        onNotify={onNotify}
        isOpen={pdfEditorOpen}
        loading={loading}
        onError={onError}
        onSuccess={onSuccess}
        setFormData={setFormData}
        jurisdictionOptions={jurisdictionOptions}
        finalizedEditMode={isFinalizedEditMode}
        finalizedEditInitialView={finalizedEditInitialView}
        externalEditLockLost={editLockLost}
        onRecoverEditLock={recoverEditLock}
      />
      <FormSettingsModal
        open={settingsModalOpen}
        onClose={handleCloseSettings}
        form={selectedFormForSettings}
        onOpenDocumentSettings={handleOpenFinalizedDocumentSettings}
        onUpdateDocument={handleUpdateFinalizedDocument}
      />
      <SyncDocumentDialog
        open={syncDialogOpen}
        form={selectedFormForSync}
        onClose={handleCloseSyncDialog}
        onSync={handleSyncDocument}
      />
      <SyncedFormEditDialog
        open={syncedEditDialogOpen}
        form={pendingSyncedEdit?.form}
        actionLabel={
          pendingSyncedEdit?.initialView === "settings"
            ? "Document Settings"
            : "Update Document"
        }
        onClose={handleCloseSyncedEditDialog}
        onAuthorize={handleAuthorizeSyncedEdit}
      />
    </Box>
  );
};

export default UploadedForms;
